import {
  Layout,
  Card,
  Tabs,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Banner,
  DropZone,
  Spinner,
  Box,
  Divider,
  Icon,
  Badge,
} from '@shopify/polaris';
import { DeleteIcon, PlayIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost, apiDelete } from '../api';

// ─── Types ────────────────────────────────────────────────────

interface MeditationAudio {
  id: string;
  mood: string;
  locale: string;
  audioUrl: string;
  fileName: string;
  durationSec: number | null;
  sortOrder: number;
  createdAt: string;
}

interface Locale {
  code: string;
  name: string;
}

const MOODS = [
  { key: 'ilusion', label: 'Ilusión', emoji: '😊' },
  { key: 'calma', label: 'Calma', emoji: '😌' },
  { key: 'apatia', label: 'Apatía', emoji: '😶' },
  { key: 'ansiedad', label: 'Ansiedad', emoji: '😰' },
  { key: 'tristeza', label: 'Tristeza', emoji: '😢' },
];

// ─── Component ────────────────────────────────────────────────

export default function MeditationsContent() {
  const [meditations, setMeditations] = useState<MeditationAudio[]>([]);
  const [locales, setLocales] = useState<Locale[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMoodIdx, setSelectedMoodIdx] = useState(0);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const selectedMood = MOODS[selectedMoodIdx].key;

  const loadData = useCallback(async () => {
    try {
      const [medRes, locRes] = await Promise.all([
        apiGet<{ meditations: MeditationAudio[] }>('/panel-api/meditations'),
        apiGet<{ locales: Locale[] }>('/panel-api/locales'),
      ]);
      setMeditations(medRes.meditations);
      setLocales(locRes.locales);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpload = useCallback(
    async (mood: string, locale: string, files: File[]) => {
      const file = files[0];
      if (!file) return;

      const uploadKey = `${mood}:${locale}`;
      setUploading(uploadKey);
      setError('');

      try {
        const { stagedTarget } = await apiPost<{
          stagedTarget: {
            url: string;
            resourceUrl: string;
            parameters: Array<{ name: string; value: string }>;
          };
        }>('/panel-api/meditations/staged-upload', {
          fileName: file.name,
          mimeType: file.type || 'audio/mpeg',
          fileSize: String(file.size),
        });

        const formData = new FormData();
        for (const param of stagedTarget.parameters) {
          formData.append(param.name, param.value);
        }
        formData.append('file', file);

        const uploadRes = await fetch(stagedTarget.url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadRes.ok) {
          throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
        }

        let durationSec: number | undefined;
        try {
          durationSec = await getAudioDuration(file);
        } catch {}

        await apiPost('/panel-api/meditations', {
          mood,
          locale,
          resourceUrl: stagedTarget.resourceUrl,
          fileName: file.name,
          durationSec,
        });

        await loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(null);
      }
    },
    [loadData],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!confirm('¿Eliminar esta meditación?')) return;
      try {
        await apiDelete(`/panel-api/meditations/${id}`);
        if (playingId === id) {
          audioRef.current?.pause();
          setPlayingId(null);
        }
        await loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Delete failed');
      }
    },
    [loadData, playingId],
  );

  const togglePlay = useCallback(
    (id: string, url: string) => {
      if (playingId === id) {
        audioRef.current?.pause();
        setPlayingId(null);
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(url);
      audio.onended = () => setPlayingId(null);
      audio.play();
      audioRef.current = audio;
      setPlayingId(id);
    },
    [playingId],
  );

  if (loading) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  const moodMeditations = meditations.filter((m) => m.mood === selectedMood);

  const tabs = MOODS.map((mood) => {
    const count = meditations.filter((m) => m.mood === mood.key).length;
    return {
      id: mood.key,
      content: `${mood.emoji} ${mood.label} (${count})`,
    };
  });

  return (
    <Layout>
      <Layout.Section>
        {error && (
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        )}

        <Card padding="0">
          <Tabs tabs={tabs} selected={selectedMoodIdx} onSelect={setSelectedMoodIdx}>
            <Box padding="400">
              <BlockStack gap="600">
                {locales.map((locale) => {
                  const localeAudios = moodMeditations.filter(
                    (m) => m.locale === locale.code,
                  );
                  const uploadKey = `${selectedMood}:${locale.code}`;
                  const isUploading = uploading === uploadKey;

                  return (
                    <LocaleSection
                      key={locale.code}
                      locale={locale}
                      audios={localeAudios}
                      isUploading={isUploading}
                      playingId={playingId}
                      onUpload={(files) => handleUpload(selectedMood, locale.code, files)}
                      onDelete={handleDelete}
                      onTogglePlay={togglePlay}
                    />
                  );
                })}
              </BlockStack>
            </Box>
          </Tabs>
        </Card>
      </Layout.Section>
    </Layout>
  );
}

// ─── Locale Section ──────────────────────────────────────────

function LocaleSection({
  locale,
  audios,
  isUploading,
  playingId,
  onUpload,
  onDelete,
  onTogglePlay,
}: {
  locale: Locale;
  audios: MeditationAudio[];
  isUploading: boolean;
  playingId: string | null;
  onUpload: (files: File[]) => void;
  onDelete: (id: string) => void;
  onTogglePlay: (id: string, url: string) => void;
}) {
  const handleDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onUpload(acceptedFiles);
      }
    },
    [onUpload],
  );

  return (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h3" variant="headingMd">
            {locale.name}
          </Text>
          <Badge>{locale.code.toUpperCase()}</Badge>
          <Text as="span" tone="subdued" variant="bodySm">
            {audios.length} {audios.length === 1 ? 'audio' : 'audios'}
          </Text>
        </InlineStack>
      </InlineStack>

      {audios.length > 0 && (
        <BlockStack gap="200">
          {audios.map((audio) => (
            <AudioRow
              key={audio.id}
              audio={audio}
              isPlaying={playingId === audio.id}
              onTogglePlay={() => onTogglePlay(audio.id, audio.audioUrl)}
              onDelete={() => onDelete(audio.id)}
            />
          ))}
        </BlockStack>
      )}

      <Box>
        {isUploading ? (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text as="span" tone="subdued">
              Subiendo audio...
            </Text>
          </InlineStack>
        ) : (
          <DropZone
            accept="audio/*"
            type="file"
            onDrop={handleDrop}
            variableHeight
          >
            <Box padding="300">
              <InlineStack align="center" gap="200">
                <Text as="span" tone="subdued" variant="bodySm">
                  Arrastra un archivo de audio o haz clic para subir
                </Text>
              </InlineStack>
            </Box>
          </DropZone>
        )}
      </Box>

      <Divider />
    </BlockStack>
  );
}

// ─── Audio Row ───────────────────────────────────────────────

function AudioRow({
  audio,
  isPlaying,
  onTogglePlay,
  onDelete,
}: {
  audio: MeditationAudio;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onDelete: () => void;
}) {
  const duration = audio.durationSec
    ? `${Math.floor(audio.durationSec / 60)}:${String(audio.durationSec % 60).padStart(2, '0')}`
    : null;

  return (
    <Box
      padding="300"
      borderRadius="200"
      background="bg-surface-secondary"
    >
      <InlineStack align="space-between" blockAlign="center" gap="200">
        <InlineStack gap="300" blockAlign="center">
          <Button
            icon={PlayIcon}
            variant={isPlaying ? 'primary' : 'plain'}
            onClick={onTogglePlay}
            accessibilityLabel={isPlaying ? 'Pause' : 'Play'}
          />
          <BlockStack gap="050">
            <Text as="span" variant="bodyMd" fontWeight="medium">
              {audio.fileName}
            </Text>
            {duration && (
              <Text as="span" variant="bodySm" tone="subdued">
                {duration}
              </Text>
            )}
          </BlockStack>
        </InlineStack>

        <Button
          icon={DeleteIcon}
          variant="plain"
          tone="critical"
          onClick={onDelete}
          accessibilityLabel="Eliminar"
        />
      </InlineStack>
    </Box>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.preload = 'metadata';

    audio.onloadedmetadata = () => {
      const duration = Math.round(audio.duration);
      URL.revokeObjectURL(audio.src);
      resolve(duration);
    };

    audio.onerror = () => {
      URL.revokeObjectURL(audio.src);
      reject(new Error('Failed to load audio metadata'));
    };

    audio.src = URL.createObjectURL(file);
  });
}
